import { useState } from 'react';

import { notify } from '../../../shared';
import { useRequest } from '../../../hooks';
import { isProd } from '../../../utilities';
import { useAppContext } from '../../../shared';
import { asLocalTime } from '../../../utilities';
import { getAgentName, addAgentToConferenceCall } from '../../../api';

const baseNormalUrl = isProd
  ? 'https://uat-cdn.nextlevel.ai/widgets/ai_conference_call.html'
  : 'https://dev-cdn.nextlevel.ai/widgets/ai_conference_call.html';
const baseVideoAvatarUrl = isProd
  ? 'https://uat-cdn.nextlevel.ai/widgets/fullscreen_video.html'
  : 'https://dev-cdn.nextlevel.ai/widgets/fullscreen_video.html';

const createUrl = (form: ConnectFormProps): string => {
  const params = new URLSearchParams({ agentId: form.agent_id });
  !isProd && params.append('useDev', 'true');

  const { bg_info_id, knowledgeBaseId, presentation_id } = form;
  bg_info_id && params.append('bg_info_id', bg_info_id);
  presentation_id && params.append('presentation_id', presentation_id);
  knowledgeBaseId && params.append('knowledgeBaseId', knowledgeBaseId);

  const { enable_video_avatar_mode: useVideoAvatar } = form;
  const baseUrl = useVideoAvatar ? baseVideoAvatarUrl : baseNormalUrl;

  return `${baseUrl}?${params.toString()}`;
};

export type ConnectFormProps = {
  agent_id: string;
  meet_link?: string;
  bot_name?: string | null;
  bg_info?: string | null;

  join_at?: string;
  bg_info_id?: string;
  presentation_id?: string;
  knowledgeBaseId?: string;

  enable_video_avatar_mode?: boolean;
};

export const useConnect = (agents: ShortAgentType[]) => {
  const { meta } = useAppContext();
  const tenantId = meta.conferenceAgentsTenantId;

  const [formId, setFormId] = useState(1);
  const [form, setForm] = useState<ConnectFormProps | null>(null);

  const [handleCall, isCalling] = useRequest(
    async _signal => {
      if (!form) return;
      const { join_at } = form || {};
      const { meet_link, bot_name, bg_info, agent_id } = form || {};
      const name = bot_name || (await getAgentName(agent_id, tenantId));

      return addAgentToConferenceCall({
        meet_link: meet_link,
        bot_name: name || undefined,
        bg_info: bg_info || undefined,
        join_at: join_at ? asLocalTime(join_at) : undefined,

        widget_host_url: createUrl(form),
      });
    },
    {
      onSuccess: () => {
        notify.success('A call has been initiated');
      },
      onError: () => {
        notify.error('Failed to initiate a call');
      },
      deps: [form, tenantId, agents],
    }
  );

  return {
    form,
    formId: String(formId),
    onChange: setForm,
    setFormId,
    setForm,
    handleCall,
    isCalling,
  };
};
